// MobileArchitectureValidator.js
// Comprehensive testing framework for mobile architecture
// Validates 100% compliance with enterprise standards

export class MobileArchitectureValidator {
    static async validateComplete() {
        console.log('🧪 COMPREHENSIVE MOBILE ARCHITECTURE VALIDATION');
        
        const results = {
            mobileDetection: this.validateMobileDetection(),
            cssConsistency: this.validateCSSConsistency(),
            scrollingFunctionality: this.validateScrolling(),
            enterprisePatterns: this.validateEnterprisePatterns(),
            desktopPreservation: this.validateDesktopPreservation(),
            pptHeroMobileHandling: this.validatePPTHeroMobile()
        };
        
        const overallScore = this.calculateOverallScore(results);
        console.log(`🏆 OVERALL ARCHITECTURE SCORE: ${overallScore}/100`);
        
        return { score: overallScore, details: results };
    }
    
    static validateMobileDetection() {
        console.log('📱 Validating mobile detection consistency...');
        
        const detectionSources = [
            window.serviceLocator?.get('MobileDetectionService'),
            window.serviceLocator?.get('UnifiedMobileSystem')?.deviceInfo
        ];
        
        const allConsistent = detectionSources.every(source => 
            source && typeof source.isMobile === 'function' || source?.isMobile !== undefined
        );
        
        return {
            passed: allConsistent,
            score: allConsistent ? 100 : 0,
            details: `Mobile detection sources: ${detectionSources.length}`
        };
    }
    
    static validateCSSConsistency() {
        console.log('🎨 Validating CSS consistency...');
        
        const criticalElements = [
            { name: 'body', element: document.body },
            { name: 'html', element: document.documentElement },
            { name: 'game-container', element: document.getElementById('game-container') },
            { name: 'canvas', element: document.querySelector('canvas') }
        ];
        
        let passedChecks = 0;
        const totalChecks = criticalElements.length;
        
        criticalElements.forEach(({ name, element }) => {
            if (element) {
                const styles = getComputedStyle(element);
                const hasProperScrolling = styles.overflow !== 'hidden' || styles.overflowY !== 'hidden';
                const hasProperTouch = styles.touchAction !== 'none';
                
                if (hasProperScrolling && hasProperTouch) {
                    passedChecks++;
                    console.log(`✅ ${name}: Proper mobile styles`);
                } else {
                    console.log(`❌ ${name}: CSS conflicts - overflow:${styles.overflow}, touch-action:${styles.touchAction}`);
                }
            }
        });
        
        const score = Math.round((passedChecks / totalChecks) * 100);
        return {
            passed: score === 100,
            score,
            details: `${passedChecks}/${totalChecks} elements have proper mobile CSS`
        };
    }
    
    static validateScrolling() {
        console.log('📜 Validating scrolling functionality...');
        
        // Test if scrolling is actually possible
        const body = document.body;
        const container = document.getElementById('game-container');
        
        const bodyScrollable = body.scrollHeight > body.clientHeight;
        const containerScrollable = container ? container.scrollHeight > container.clientHeight : false;
        
        const scrollingWorks = bodyScrollable || containerScrollable;
        
        return {
            passed: scrollingWorks,
            score: scrollingWorks ? 100 : 0,
            details: `Body scrollable: ${bodyScrollable}, Container scrollable: ${containerScrollable}`
        };
    }
    
    static validateEnterprisePatterns() {
        console.log('🏗️ Validating enterprise patterns...');
        
        const patterns = [
            { name: 'Service Locator', check: !!window.serviceLocator },
            { name: 'Singleton Pattern', check: !!window.serviceLocator?.get('UnifiedMobileSystem') },
            { name: 'Error Boundaries', check: typeof EnterpriseErrorBoundary !== 'undefined' },
            { name: 'Configuration Constants', check: typeof MOBILE_CONSTANTS !== 'undefined' }
        ];
        
        const passedPatterns = patterns.filter(p => p.check).length;
        const score = Math.round((passedPatterns / patterns.length) * 100);
        
        return {
            passed: score === 100,
            score,
            details: `${passedPatterns}/${patterns.length} enterprise patterns implemented`
        };
    }
    
    static validateDesktopPreservation() {
        console.log('🖥️ Validating desktop preservation...');
        
        const unifiedSystem = window.serviceLocator?.get('UnifiedMobileSystem');
        const mobileSystemRunning = unifiedSystem?.isInitialized;
        const deviceInfo = unifiedSystem?.deviceInfo;
        
        // On desktop, mobile system should not be running
        const isDesktop = !deviceInfo?.isMobile;
        const desktopPreserved = isDesktop ? !mobileSystemRunning : true;
        
        return {
            passed: desktopPreserved,
            score: desktopPreserved ? 100 : 0,
            details: `Desktop device: ${isDesktop}, Mobile system running: ${mobileSystemRunning}`
        };
    }
    
    static validatePPTHeroMobile() {
        console.log('🎮 Validating PPT Hero mobile handling...');
        
        // Check if PPT Hero scene has mobile notification
        const pptHeroScene = window.game?.scene?.getScene('PPTHeroScene');
        const hasDesktopNotification = pptHeroScene && typeof pptHeroScene.showDesktopRecommendation === 'function';
        
        return {
            passed: hasDesktopNotification,
            score: hasDesktopNotification ? 100 : 0,
            details: `PPT Hero desktop notification: ${hasDesktopNotification ? 'Implemented' : 'Missing'}`
        };
    }
    
    static calculateOverallScore(results) {
        const scores = Object.values(results).map(r => r.score);
        return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
    }
}

// Make available globally for testing
window.validateMobileArchitecture = () => MobileArchitectureValidator.validateComplete();
